import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import { AdminLayout } from "@/pages/AdminDashboard";
import api from "@/services/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit2, Check, X, ExternalLink,
  Play, Video, Type, Phone, Save, User, Star, MessageSquare, Award, LayoutGrid, Upload
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return m ? m[1] : null;
}

function YouTubeThumb({ url, className = "" }) {
  const id = extractYouTubeId(url);
  if (!id) return (
    <div className={`bg-white/5 rounded flex items-center justify-center ${className}`}>
      <Play className="w-6 h-6 text-gray-500" />
    </div>
  );
  return (
    <img
      src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`}
      alt="thumbnail"
      className={`object-cover rounded ${className}`}
      onError={(e) => { e.target.style.display = "none"; }}
    />
  );
}

// ─── Image upload (stores URL returned by API after file save) ────────────────
function LandingImageUpload({ token, label, value, onUploaded, disabled }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const r = await api.uploadLandingImage(file, token);
    setUploading(false);
    e.target.value = "";
    if (r?.url) {
      onUploaded(r.url);
      toast.success("Image uploaded");
    } else {
      toast.error(r?.error || "Upload failed");
    }
  };
  return (
    <div className="mb-4">
      <label className="block text-xs text-gray-400 mb-1.5 font-medium uppercase tracking-wide">{label}</label>
      <div className="flex flex-wrap items-center gap-3">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={pick} disabled={disabled || uploading} />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white bg-white/10 border border-white/15 hover:bg-white/15 disabled:opacity-50"
        >
          <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload image"}
        </button>
        {value ? (
          <span className="text-xs text-gray-500 truncate max-w-md" title={value}>Saved</span>
        ) : (
          <span className="text-xs text-gray-600">No image yet</span>
        )}
      </div>
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/10">
        <div className="w-9 h-9 rounded-lg bg-[#c4646a]/15 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#c4646a]" />
        </div>
        <h2 className="text-lg font-medium text-white">{title}</h2>
      </div>
      {children}
    </div>
  );
}

// ─── Inline editable text field ───────────────────────────────────────────────
function TextField({ label, value, onChange, multiline = false, placeholder = "" }) {
  return (
    <div className="mb-4">
      <label className="block text-xs text-gray-400 mb-1.5 font-medium uppercase tracking-wide">{label}</label>
      {multiline ? (
        <textarea
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm resize-none focus:outline-none focus:border-[#c4646a]/50"
          rows={4}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      ) : (
        <input
          type="text"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#c4646a]/50"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

// ─── YouTube URL row (reels / shorts) ────────────────────────────────────────
function YoutubeRow({ item, onSave, onDelete, index }) {
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(item.youtube_url || "");
  const [title, setTitle] = useState(item.title || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!url.trim()) { toast.error("YouTube URL is required"); return; }
    setSaving(true);
    await onSave({ ...item, youtube_url: url.trim(), title: title.trim() || `Video ${index + 1}` });
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-white/3 rounded-lg border border-white/7 group">
      <YouTubeThumb url={item.youtube_url} className="w-16 h-10 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="space-y-1.5">
            <input
              className="w-full bg-white/10 border border-white/15 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-[#c4646a]/50"
              value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
            />
            <input
              className="w-full bg-white/10 border border-white/15 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-[#c4646a]/50"
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Title"
            />
          </div>
        ) : (
          <>
            <p className="text-white text-sm font-medium truncate">{item.title || `Video ${index + 1}`}</p>
            <a href={item.youtube_url} target="_blank" rel="noopener noreferrer"
              className="text-gray-500 text-xs hover:text-[#c4646a] flex items-center gap-1 truncate">
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
              {item.youtube_url}
            </a>
          </>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {editing ? (
          <>
            <button onClick={handleSave} disabled={saving}
              className="p-1.5 rounded bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors">
              <Check className="w-4 h-4" />
            </button>
            <button onClick={() => { setEditing(false); setUrl(item.youtube_url); setTitle(item.title); }}
              className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-gray-400 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button onClick={() => setEditing(true)}
            className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-gray-400 transition-colors opacity-0 group-hover:opacity-100">
            <Edit2 className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => onDelete(item.id)}
          className="p-1.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors opacity-0 group-hover:opacity-100">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Add new YouTube row ──────────────────────────────────────────────────────
function AddYoutubeRow({ onAdd, placeholder = "https://youtube.com/watch?v=..." }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);

  const handleAdd = async () => {
    if (!url.trim()) { toast.error("YouTube URL is required"); return; }
    setAdding(true);
    await onAdd({ youtube_url: url.trim(), title: title.trim() || "Untitled", order: 0 });
    setUrl(""); setTitle(""); setOpen(false);
    setAdding(false);
  };

  if (!open) return (
    <button onClick={() => setOpen(true)}
      className="flex items-center gap-2 text-sm text-[#c4646a] hover:text-[#d4848a] transition-colors mt-3">
      <Plus className="w-4 h-4" /> Add Video
    </button>
  );

  return (
    <div className="mt-3 p-3 bg-white/3 rounded-lg border border-[#c4646a]/20 space-y-2">
      <input
        className="w-full bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#c4646a]/50"
        value={url} onChange={(e) => setUrl(e.target.value)}
        placeholder={placeholder}
      />
      <input
        className="w-full bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#c4646a]/50"
        value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (optional)"
      />
      <div className="flex gap-2">
        <button onClick={handleAdd} disabled={adding}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm text-white font-medium transition-colors"
          style={{ background: "linear-gradient(135deg, #c4646a, #a85459)" }}>
          <Plus className="w-3.5 h-3.5" /> {adding ? "Adding..." : "Add"}
        </button>
        <button onClick={() => { setOpen(false); setUrl(""); setTitle(""); }}
          className="px-3 py-1.5 rounded text-sm text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Testimonial Row ──────────────────────────────────────────────────────────
function TestimonialRow({ item, onSave, onDelete, index }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name || "");
  const [location, setLocation] = useState(item.location || "");
  const [text, setText] = useState(item.text || "");
  const [stars, setStars] = useState(item.stars || 5);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !text.trim()) { toast.error("Name and review text are required"); return; }
    setSaving(true);
    await onSave({ ...item, name: name.trim(), location: location.trim(), text: text.trim(), stars: Number(stars) });
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="p-4 bg-white/3 rounded-lg border border-white/7 group">
      {editing ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input
              className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#c4646a]/50"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Couple name (e.g. Priya & Rahul)"
            />
            <input
              className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#c4646a]/50"
              value={location} onChange={(e) => setLocation(e.target.value)}
              placeholder="Location (e.g. Goa Destination Wedding)"
            />
          </div>
          <textarea
            className="w-full bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#c4646a]/50 resize-none"
            rows={3}
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Review text..."
          />
          <div className="flex items-center gap-3">
            <label className="text-xs text-gray-400">Stars:</label>
            <select
              className="bg-white/10 border border-white/15 rounded px-2 py-1 text-white text-sm focus:outline-none"
              value={stars} onChange={(e) => setStars(e.target.value)}>
              {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} ★</option>)}
            </select>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm text-white font-medium"
              style={{ background: "linear-gradient(135deg, #c4646a, #a85459)" }}>
              <Check className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save"}
            </button>
            <button onClick={() => { setEditing(false); setName(item.name); setLocation(item.location); setText(item.text); setStars(item.stars); }}
              className="px-3 py-1.5 rounded text-sm text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #c4646a, #a85459)" }}>
            {(item.name || "?")[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-white text-sm font-medium">{item.name}</p>
              <span className="text-gray-500 text-xs">·</span>
              <p className="text-gray-500 text-xs">{item.location}</p>
            </div>
            <div className="flex gap-0.5 mb-1">
              {[...Array(Number(item.stars) || 5)].map((_, i) => (
                <Star key={i} className="w-3 h-3 fill-[#c4646a] text-[#c4646a]" />
              ))}
            </div>
            <p className="text-gray-400 text-sm leading-relaxed line-clamp-2">"{item.text}"</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => setEditing(true)}
              className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-gray-400 transition-colors">
              <Edit2 className="w-4 h-4" />
            </button>
            <button onClick={() => onDelete(item.id)}
              className="p-1.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Add Testimonial ──────────────────────────────────────────────────────────
function AddTestimonialRow({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [text, setText] = useState("");
  const [stars, setStars] = useState(5);
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!name.trim() || !text.trim()) { toast.error("Name and review text are required"); return; }
    setAdding(true);
    await onAdd({ name: name.trim(), location: location.trim(), text: text.trim(), stars: Number(stars), order: 0 });
    setName(""); setLocation(""); setText(""); setStars(5); setOpen(false);
    setAdding(false);
  };

  if (!open) return (
    <button onClick={() => setOpen(true)}
      className="flex items-center gap-2 text-sm text-[#c4646a] hover:text-[#d4848a] transition-colors mt-3">
      <Plus className="w-4 h-4" /> Add Testimonial
    </button>
  );

  return (
    <div className="mt-3 p-4 bg-white/3 rounded-lg border border-[#c4646a]/20 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input
          className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#c4646a]/50"
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Couple name (e.g. Priya & Rahul)"
        />
        <input
          className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#c4646a]/50"
          value={location} onChange={(e) => setLocation(e.target.value)}
          placeholder="Location (e.g. Goa Destination Wedding)"
        />
      </div>
      <textarea
        className="w-full bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#c4646a]/50 resize-none"
        rows={3}
        value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Review text..."
      />
      <div className="flex items-center gap-3">
        <label className="text-xs text-gray-400">Stars:</label>
        <select
          className="bg-white/10 border border-white/15 rounded px-2 py-1 text-white text-sm focus:outline-none"
          value={stars} onChange={(e) => setStars(e.target.value)}>
          {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} ★</option>)}
        </select>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={handleAdd} disabled={adding}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm text-white font-medium"
          style={{ background: "linear-gradient(135deg, #c4646a, #a85459)" }}>
          <Plus className="w-3.5 h-3.5" /> {adding ? "Adding..." : "Add"}
        </button>
        <button onClick={() => { setOpen(false); setName(""); setLocation(""); setText(""); setStars(5); }}
          className="px-3 py-1.5 rounded text-sm text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Award Row ────────────────────────────────────────────────────────────────
function AwardRow({ item, onSave, onDelete, index, token }) {
  const [editing, setEditing] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(item.photo_url || "");
  const [title, setTitle] = useState(item.title || "");
  const [year, setYear] = useState(item.year || "");
  const [description, setDescription] = useState(item.description || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPhotoUrl(item.photo_url || "");
    setTitle(item.title || "");
    setYear(item.year || "");
    setDescription(item.description || "");
  }, [item]);

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    await onSave({ ...item, photo_url: photoUrl.trim(), title: title.trim(), year: year.trim(), description: description.trim() });
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="p-4 bg-white/3 rounded-lg border border-white/7 group flex items-start gap-4">
      {item.photo_url ? (
        <img src={item.photo_url} alt="" className="w-16 h-16 object-cover rounded-lg flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
      ) : (
        <div className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
          <Award className="w-8 h-8 text-gray-500" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="space-y-2">
            <LandingImageUpload
              token={token}
              label="Award photo"
              value={photoUrl}
              onUploaded={setPhotoUrl}
            />
            <div className="grid grid-cols-2 gap-2">
              <input className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
              <input className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm" value={year} onChange={(e) => setYear(e.target.value)} placeholder="Year" />
            </div>
            <textarea className="w-full bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm resize-none" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" />
            <div className="flex gap-2 pt-1">
              <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm text-white font-medium" style={{ background: "linear-gradient(135deg, #c4646a, #a85459)" }}>
                <Check className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save"}
              </button>
              <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded text-sm text-gray-400 hover:text-white bg-white/5">Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-white font-medium">{item.title || "Untitled"}{item.year ? ` (${item.year})` : ""}</p>
            {item.description && <p className="text-gray-400 text-sm mt-0.5 line-clamp-2">{item.description}</p>}
            <div className="flex gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs text-[#c4646a] hover:underline"><Edit2 className="w-3 h-3" /> Edit</button>
              <button onClick={() => window.confirm("Delete this award?") && onDelete(item.id)} className="flex items-center gap-1 text-xs text-red-400 hover:underline"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AddAwardRow({ onAdd, token }) {
  const [open, setOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [title, setTitle] = useState("");
  const [year, setYear] = useState("");
  const [description, setDescription] = useState("");
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setAdding(true);
    await onAdd({ photo_url: photoUrl.trim(), title: title.trim(), year: year.trim(), description: description.trim(), order: 0 });
    setPhotoUrl(""); setTitle(""); setYear(""); setDescription(""); setOpen(false);
    setAdding(false);
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm text-[#c4646a] hover:text-[#d4848a] transition-colors mt-3">
      <Plus className="w-4 h-4" /> Add Award
    </button>
  );

  return (
    <div className="mt-3 p-4 bg-white/3 rounded-lg border border-[#c4646a]/20 space-y-2">
      <LandingImageUpload token={token} label="Award photo" value={photoUrl} onUploaded={setPhotoUrl} />
      <div className="grid grid-cols-2 gap-2">
        <input className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
        <input className="bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm" value={year} onChange={(e) => setYear(e.target.value)} placeholder="Year" />
      </div>
      <textarea className="w-full bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm resize-none" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" />
      <div className="flex gap-2 pt-1">
        <button onClick={handleAdd} disabled={adding} className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm text-white font-medium" style={{ background: "linear-gradient(135deg, #c4646a, #a85459)" }}>
          <Plus className="w-3.5 h-3.5" /> {adding ? "Adding..." : "Add"}
        </button>
        <button onClick={() => { setOpen(false); setPhotoUrl(""); setTitle(""); setYear(""); setDescription(""); }} className="px-3 py-1.5 rounded text-sm text-gray-400 hover:text-white bg-white/5">Cancel</button>
      </div>
    </div>
  );
}


// ─── Footer logo row ─────────────────────────────────────────────────────────
function FooterLogoRow({ item, onSave, onDelete, index, token }) {
  const [editing, setEditing] = useState(false);
  const [imageUrl, setImageUrl] = useState(item.image_url || "");
  const [linkUrl, setLinkUrl] = useState(item.link_url || "");
  const [order, setOrder] = useState(String(item.order ?? 0));
  const [active, setActive] = useState(item.is_active !== 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setImageUrl(item.image_url || "");
    setLinkUrl(item.link_url || "");
    setOrder(String(item.order ?? 0));
    setActive(item.is_active !== 0);
  }, [item]);

  const handleSave = async () => {
    if (!imageUrl.trim()) { toast.error("Upload an image first"); return; }
    setSaving(true);
    await onSave({
      ...item,
      image_url: imageUrl.trim(),
      link_url: linkUrl.trim(),
      order: parseInt(order, 10) || 0,
      is_active: active ? 1 : 0,
    });
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="p-4 bg-white/3 rounded-lg border border-white/7 group flex items-start gap-4">
      {item.image_url ? (
        <img src={item.image_url} alt="" className="w-16 h-16 object-contain rounded-lg flex-shrink-0 bg-white/5" onError={(e) => { e.target.style.display = "none"; }} />
      ) : (
        <div className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
          <LayoutGrid className="w-8 h-8 text-gray-500" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="space-y-2">
            <LandingImageUpload token={token} label="Logo image" value={imageUrl} onUploaded={setImageUrl} />
            <input className="w-full bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="Optional link URL (opens in new tab)" />
            <div className="flex gap-2 items-center">
              <label className="text-xs text-gray-400">Order</label>
              <input type="number" className="w-20 bg-white/10 border border-white/15 rounded px-2 py-1 text-white text-sm" value={order} onChange={(e) => setOrder(e.target.value)} />
              <label className="flex items-center gap-2 text-xs text-gray-400 ml-2">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active
              </label>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm text-white font-medium" style={{ background: "linear-gradient(135deg, #c4646a, #a85459)" }}>
                <Check className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save"}
              </button>
              <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded text-sm text-gray-400 hover:text-white bg-white/5">Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-white font-medium truncate">{item.link_url ? "Linked logo" : "Logo"} #{index + 1}</p>
            {item.link_url && <p className="text-gray-500 text-xs truncate mt-0.5">{item.link_url}</p>}
            <div className="flex gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-xs text-[#c4646a] hover:underline"><Edit2 className="w-3 h-3" /> Edit</button>
              <button onClick={() => window.confirm("Delete this logo?") && onDelete(item.id)} className="flex items-center gap-1 text-xs text-red-400 hover:underline"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AddFooterLogoRow({ onAdd, token }) {
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!imageUrl.trim()) { toast.error("Upload an image first"); return; }
    setAdding(true);
    await onAdd({ image_url: imageUrl.trim(), link_url: linkUrl.trim(), order: 0 });
    setImageUrl(""); setLinkUrl(""); setOpen(false);
    setAdding(false);
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm text-[#c4646a] hover:text-[#d4848a] transition-colors mt-3">
      <Plus className="w-4 h-4" /> Add Footer Logo
    </button>
  );

  return (
    <div className="mt-3 p-4 bg-white/3 rounded-lg border border-[#c4646a]/20 space-y-2">
      <LandingImageUpload token={token} label="Logo image" value={imageUrl} onUploaded={setImageUrl} />
      <input className="w-full bg-white/10 border border-white/15 rounded px-2 py-1.5 text-white text-sm" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="Optional: link when logo is clicked" />
      <div className="flex gap-2 pt-1">
        <button onClick={handleAdd} disabled={adding} className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm text-white font-medium" style={{ background: "linear-gradient(135deg, #c4646a, #a85459)" }}>
          <Plus className="w-3.5 h-3.5" /> {adding ? "Adding..." : "Add"}
        </button>
        <button onClick={() => { setOpen(false); setImageUrl(""); setLinkUrl(""); }} className="px-3 py-1.5 rounded text-sm text-gray-400 hover:text-white bg-white/5">Cancel</button>
      </div>
    </div>
  );
}


// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminLandingPage() {
  const { token } = useAuth();

  // Content fields
  const [content, setContent] = useState({
    hero_headline: "Looking for a Destination Wedding Planner?",
    hero_subtext: "We are an award winning wedding planner known for destination wedding planning, innovative decor conceptualization, flawless management, and personalized end-to-end wedding services.",
    about_text1: "Since our establishment in 2001, Inventive Pages has been dedicated to transforming your dreams into reality through our comprehensive services in pre-wedding planning, wedding coordination, stunning decoration, and flawless event management.",
    about_text2: "With a deep passion for crafting unforgettable celebrations, we specialize in selecting elegant venues and designing experiences that add a true wow factor to your big day.",
    founder_name: "HETTNA BUNDELA",
    founder_title: "Founder — Inventive Pages",
    founder_badge: "Est. 2001 · 23 Years of Excellence",
    founder_image_url: "https://inventivepages.com/wp-content/uploads/2024/07/hetna-bundela.jpg",
    founders: [],
    phone1: "+91 9638963850",
    email1: "info@inventivepages.com",
    phone2: "+91 98989 89863",
    email2: "ankitbundela@gmail.com",
    phone3: "+91 9898907080",
    email3: "hetna.13@gmail.com",
    contact_address: "607 - B, Near BPCL Petrol Pump, Karamchari Nagar, Ahmedabad – 380013, Gujarat, India",
    instagram_url: "https://instagram.com/Inventivepages",
    behance_url: "https://behance.net/inventivepages",
    youtube_url: "https://www.youtube.com/@inventivepages",
    whatsapp_number: "919638963850",
  });
  const [contentSaving, setContentSaving] = useState(false);

  // Reels
  const [reels, setReels] = useState([]);
  const [reelsLoading, setReelsLoading] = useState(true);

  // Gallery Shorts
  const [shorts, setShorts] = useState([]);
  const [shortsLoading, setShortsLoading] = useState(true);

  // Testimonials
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);

  // Awards
  const [awards, setAwards] = useState([]);
  const [awardsLoading, setAwardsLoading] = useState(true);

  // Footer logos
  const [footerLogos, setFooterLogos] = useState([]);
  const [footerLogosLoading, setFooterLogosLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    try {
      const [contentRes, reelsRes, shortsRes, testimonialsRes, awardsRes, footerRes] = await Promise.all([
        api.getLandingContent(token),
        api.getReels(token),
        api.getGalleryShorts(token),
        api.getTestimonials(token),
        api.getAwards(token),
        api.getFooterLogos(token),
      ]);
      if (contentRes?.content) {
        setContent(prev => ({ ...prev, ...contentRes.content }));
      }
      if (reelsRes?.reels) setReels(reelsRes.reels);
      if (shortsRes?.shorts) setShorts(shortsRes.shorts);
      if (testimonialsRes?.testimonials) setTestimonials(testimonialsRes.testimonials);
      if (awardsRes?.awards) setAwards(awardsRes.awards);
      if (footerRes?.footer_logos) setFooterLogos(footerRes.footer_logos);
    } catch (e) {
      console.error(e);
    } finally {
      setReelsLoading(false);
      setShortsLoading(false);
      setTestimonialsLoading(false);
      setAwardsLoading(false);
      setFooterLogosLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveContent = async () => {
    setContentSaving(true);
    const res = await api.saveLandingContent(content, token);
    if (res?.success) toast.success("Content saved!");
    else toast.error("Failed to save content");
    setContentSaving(false);
  };

  // Reels CRUD
  const addReel = async (data) => {
    const res = await api.addReel(data, token);
    if (res?.success) { toast.success("Video added!"); fetchAll(); }
    else toast.error("Failed to add video");
  };
  const updateReel = async (data) => {
    const res = await api.updateReel(data, token);
    if (res?.success) { toast.success("Video updated!"); fetchAll(); }
    else toast.error("Failed to update video");
  };
  const deleteReel = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    const res = await api.deleteReel(id, token);
    if (res?.success) { toast.success("Video deleted!"); fetchAll(); }
    else toast.error("Failed to delete video");
  };

  // Shorts CRUD
  const addShort = async (data) => {
    const res = await api.addGalleryShort(data, token);
    if (res?.success) { toast.success("Short added!"); fetchAll(); }
    else toast.error("Failed to add short");
  };
  const updateShort = async (data) => {
    const res = await api.updateGalleryShort(data, token);
    if (res?.success) { toast.success("Short updated!"); fetchAll(); }
    else toast.error("Failed to update short");
  };
  const deleteShort = async (id) => {
    if (!window.confirm("Delete this short?")) return;
    const res = await api.deleteGalleryShort(id, token);
    if (res?.success) { toast.success("Short deleted!"); fetchAll(); }
    else toast.error("Failed to delete short");
  };

  // Testimonials CRUD
  const addTestimonial = async (data) => {
    const res = await api.addTestimonial(data, token);
    if (res?.success) { toast.success("Testimonial added!"); fetchAll(); }
    else toast.error("Failed to add testimonial");
  };
  const updateTestimonial = async (data) => {
    const res = await api.updateTestimonial(data, token);
    if (res?.success) { toast.success("Testimonial updated!"); fetchAll(); }
    else toast.error("Failed to update testimonial");
  };
  const deleteTestimonial = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    const res = await api.deleteTestimonial(id, token);
    if (res?.success) { toast.success("Testimonial deleted!"); fetchAll(); }
    else toast.error("Failed to delete testimonial");
  };

  // Awards CRUD
  const addAward = async (data) => {
    const res = await api.addAward(data, token);
    if (res?.success) { toast.success("Award added!"); fetchAll(); }
    else toast.error("Failed to add award");
  };
  const updateAward = async (data) => {
    const res = await api.updateAward(data, token);
    if (res?.success) { toast.success("Award updated!"); fetchAll(); }
    else toast.error("Failed to update award");
  };
  const deleteAward = async (id) => {
    if (!window.confirm("Delete this award?")) return;
    const res = await api.deleteAward(id, token);
    if (res?.success) { toast.success("Award deleted!"); fetchAll(); }
    else toast.error("Failed to delete award");
  };

  const addFooterLogo = async (data) => {
    const res = await api.addFooterLogo(data, token);
    if (res?.success) { toast.success("Footer logo added!"); fetchAll(); }
    else toast.error("Failed to add logo");
  };
  const updateFooterLogo = async (data) => {
    const res = await api.updateFooterLogo(data, token);
    if (res?.success) { toast.success("Logo updated!"); fetchAll(); }
    else toast.error("Failed to update logo");
  };
  const deleteFooterLogo = async (id) => {
    if (!window.confirm("Delete this footer logo?")) return;
    const res = await api.deleteFooterLogo(id, token);
    if (res?.success) { toast.success("Logo deleted!"); fetchAll(); }
    else toast.error("Failed to delete logo");
  };

  const setField = (key) => (val) => setContent(prev => ({ ...prev, [key]: val }));

  return (
    <AdminLayout>
      <div className="p-6 md:p-8">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-light text-white">Landing Page</h1>
            <p className="text-gray-400 mt-1 text-sm">Manage content, videos, gallery shorts, testimonials, and founder info shown on the public landing page.</p>
          </div>
          <a href="/" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors">
            <ExternalLink className="w-4 h-4" /> Preview
          </a>
        </div>

        {/* ── Hero Text ── */}
        <Section title="Hero Section" icon={Type}>
          <TextField label="Headline" value={content.hero_headline} onChange={setField("hero_headline")} />
          <TextField label="Sub-text" value={content.hero_subtext} onChange={setField("hero_subtext")} multiline />
        </Section>

        {/* ── Founder / About ── */}
        <Section title="Founder & About" icon={User}>
          <p className="text-gray-500 text-sm mb-4">Update founders (max 2) shown in the About section. Add or remove founder boxes.</p>
          {(Array.isArray(content.founders) ? content.founders : []).map((f, i) => (
            <div key={i} className="mb-6 p-4 rounded-xl border border-white/10 bg-white/3">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-gray-400">Founder {i + 1}</span>
                <button
                  type="button"
                  onClick={() => setContent(prev => ({
                    ...prev,
                    founders: (prev.founders || []).filter((_, j) => j !== i)
                  }))}
                  className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
                >
                  <Trash2 className="w-4 h-4" /> Remove
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-x-6">
                <TextField
                  label="Founder Name"
                  value={f.name || ""}
                  onChange={(v) => setContent(prev => ({
                    ...prev,
                    founders: (prev.founders || []).map((x, j) => j === i ? { ...x, name: v } : x)
                  }))}
                  placeholder="HETTNA BUNDELA"
                />
                <TextField
                  label="Founder Title / Designation"
                  value={f.title || ""}
                  onChange={(v) => setContent(prev => ({
                    ...prev,
                    founders: (prev.founders || []).map((x, j) => j === i ? { ...x, title: v } : x)
                  }))}
                  placeholder="Founder — Inventive Pages"
                />
              </div>
              <TextField
                label="Badge Text (below photo)"
                value={f.badge || ""}
                onChange={(v) => setContent(prev => ({
                  ...prev,
                  founders: (prev.founders || []).map((x, j) => j === i ? { ...x, badge: v } : x)
                }))}
                placeholder="Est. 2001 · 23 Years of Excellence"
              />
              <LandingImageUpload
                token={token}
                label="Founder photo"
                value={f.image_url || ""}
                onUploaded={(url) => setContent(prev => ({
                  ...prev,
                  founders: (prev.founders || []).map((x, j) => j === i ? { ...x, image_url: url } : x)
                }))}
              />
              {f.image_url && (
                <div className="mb-4">
                  <img
                    src={f.image_url}
                    alt="Founder preview"
                    className="w-24 h-28 object-cover rounded-xl border border-white/10"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
              )}
            </div>
          ))}
          {(content.founders || []).length < 2 && (
            <button
              type="button"
              onClick={() => setContent(prev => ({
                ...prev,
                founders: [...(prev.founders || []), { name: "", title: "", image_url: "", badge: "" }]
              }))}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-[#c4646a] border border-[#c4646a]/40 hover:bg-[#c4646a]/10"
            >
              <Plus className="w-4 h-4" /> Add Founder
            </button>
          )}
          {(content.founders || []).length === 0 && (
            <p className="text-gray-500 text-xs mt-2">No founders yet. Add one to display in the About section.</p>
          )}
          <div className="mt-6 pt-6 border-t border-white/10">
            <TextField label="About Paragraph 1" value={content.about_text1} onChange={setField("about_text1")} multiline />
            <TextField label="About Paragraph 2" value={content.about_text2} onChange={setField("about_text2")} multiline />
          </div>
        </Section>

        {/* ── Contact ── */}
        <Section title="Contact Details" icon={Phone}>
          <div className="grid md:grid-cols-2 gap-x-6">
            <div>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-3">Main Office</p>
              <TextField label="Phone" value={content.phone1} onChange={setField("phone1")} />
              <TextField label="Email" value={content.email1} onChange={setField("email1")} />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-3">Aankiit Bundela</p>
              <TextField label="Phone" value={content.phone2} onChange={setField("phone2")} />
              <TextField label="Email" value={content.email2} onChange={setField("email2")} />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-3">Hettna Bundela</p>
              <TextField label="Phone" value={content.phone3} onChange={setField("phone3")} />
              <TextField label="Email" value={content.email3} onChange={setField("email3")} />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-3">Social / WhatsApp</p>
              <TextField label="Instagram URL" value={content.instagram_url} onChange={setField("instagram_url")} />
              <TextField label="Behance URL" value={content.behance_url} onChange={setField("behance_url")} />
              <TextField label="YouTube URL" value={content.youtube_url} onChange={setField("youtube_url")} />
              <TextField label="WhatsApp Number (no +)" value={content.whatsapp_number} onChange={setField("whatsapp_number")} />
            </div>
          </div>
          <TextField label="Address" value={content.contact_address} onChange={setField("contact_address")} multiline />
        </Section>

        {/* Save content button */}
        <div className="flex justify-end mb-8">
          <button onClick={saveContent} disabled={contentSaving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-white font-medium transition-all hover:opacity-90"
            style={{ background: "linear-gradient(135deg, #c4646a, #a85459)" }}>
            <Save className="w-4 h-4" />
            {contentSaving ? "Saving..." : "Save All Content"}
          </button>
        </div>

        {/* ── Videos (Reels) ── */}
        <Section title="Videos — About Section" icon={Video}>
          <p className="text-gray-500 text-sm mb-4">These appear in the About Us section. Paste any YouTube URL (watch, shorts, embed).</p>
          {reelsLoading ? (
            <div className="text-gray-500 text-sm">Loading...</div>
          ) : (
            <div className="space-y-2">
              {reels.map((reel, i) => (
                <YoutubeRow key={reel.id} item={reel} index={i} onSave={updateReel} onDelete={deleteReel} />
              ))}
              {reels.length < 4 && <AddYoutubeRow onAdd={addReel} />}
            </div>
          )}
        </Section>

        {/* ── Gallery Shorts ── */}
        <Section title="Gallery — YouTube Shorts" icon={Play}>
          <p className="text-gray-500 text-sm mb-4">These appear in the Memorable Moments / Gallery section as portrait-format YouTube Shorts.</p>
          {shortsLoading ? (
            <div className="text-gray-500 text-sm">Loading...</div>
          ) : (
            <div className="space-y-2">
              {shorts.map((short, i) => (
                <YoutubeRow key={short.id} item={short} index={i} onSave={updateShort} onDelete={deleteShort} />
              ))}
              {shorts.length < 10 && <AddYoutubeRow onAdd={addShort} placeholder="https://youtube.com/shorts/..." />}
            </div>
          )}
        </Section>

        {/* ── Testimonials ── */}
        <Section title="Testimonials" icon={MessageSquare}>
          <p className="text-gray-500 text-sm mb-4">Manage couple reviews shown in the Testimonials section. Hover over a row to edit or delete.</p>
          {testimonialsLoading ? (
            <div className="text-gray-500 text-sm">Loading...</div>
          ) : (
            <div className="space-y-3">
              {testimonials.map((t, i) => (
                <TestimonialRow key={t.id} item={t} index={i} onSave={updateTestimonial} onDelete={deleteTestimonial} />
              ))}
              <AddTestimonialRow onAdd={addTestimonial} />
            </div>
          )}
        </Section>

        {/* ── Awards & Recognitions ── */}
        <Section title="Awards & Recognitions" icon={Award}>
          <p className="text-gray-500 text-sm mb-4">Manage awards shown after Testimonials. Upload an image for each award, then set title, year, and description. Displayed in a horizontal row on the site; visitors can tap for a larger view.</p>
          {awardsLoading ? (
            <div className="text-gray-500 text-sm">Loading...</div>
          ) : (
            <div className="space-y-3">
              {awards.map((a, i) => (
                <AwardRow key={a.id} item={a} index={i} onSave={updateAward} onDelete={deleteAward} token={token} />
              ))}
              <AddAwardRow onAdd={addAward} token={token} />
            </div>
          )}
        </Section>

        {/* ── Footer partner logos ── */}
        <Section title="Footer — Partner Logos" icon={LayoutGrid}>
          <p className="text-gray-500 text-sm mb-4">Images shown above the footer tagline. Use <strong>Upload image</strong> for each logo (files are stored on this server under <code className="text-gray-400">public/uploads/landing/</code>). Optional link opens in a new tab when visitors click the logo. Default seven partner logos are pre-seeded; edit or replace them here.</p>
          {footerLogosLoading ? (
            <div className="text-gray-500 text-sm">Loading...</div>
          ) : (
            <div className="space-y-3">
              {footerLogos.map((row, i) => (
                <FooterLogoRow key={row.id} item={row} index={i} onSave={updateFooterLogo} onDelete={deleteFooterLogo} token={token} />
              ))}
              <AddFooterLogoRow onAdd={addFooterLogo} token={token} />
            </div>
          )}
        </Section>
      </div>
    </AdminLayout>
  );
}
